export enum IntegrationTokenRoleCode {
  Admin = 'ADMIN',
  ReadOnly = 'READ_ONLY',
  NoAccess = 'NO_ACCESS'
}

export interface IntegrationTokenProjectAccess {
  projectId: string;
  roleCode: 'OWNER' | 'ADMIN' | 'READ_ONLY'; // Project-level role
}

export interface IntegrationToken {
  id: string;
  clientId: string;
  name: string;
  roleCode: IntegrationTokenRoleCode; // Primary role
  canCreateProjects?: boolean; // Only for NO_ACCESS
  createdByUser: string; // User ID of token creator
  created: string;
  lastUpdate: string;
  projects: IntegrationTokenProjectAccess[];
}

export interface IntegrationTokenWithSecret extends IntegrationToken {
  token: string;
}

export interface IntegrationTokenListResponse {
  list: IntegrationToken[];
}

export interface IntegrationTokenRequest {
  name: string;
  roleCode: IntegrationTokenRoleCode;
  canCreateProjects?: boolean;
  projects: IntegrationTokenProjectAccess[];
}

// ── Delegations ──────────────────────────────────────────────────────────────
// A delegation is a permission blueprint attached to an integration token
// (`tokenId`) that lets that token create ONE new token with the pre-defined
// scope below. Used by ZCP during production hand-over. Delegations may use any
// of the five roles (unlike the token UI, which exposes only three), so the
// display types below cover the full range.

export enum IntegrationTokenDelegationRoleCode {
  Owner = 'OWNER',
  Admin = 'ADMIN',
  BasicUser = 'BASIC_USER',
  ReadOnly = 'READ_ONLY',
  NoAccess = 'NO_ACCESS'
}

export interface IntegrationTokenDelegationProjectPermission {
  projectId: string;
  clientId?: string; // present on read, not required on create
  roleCode: IntegrationTokenDelegationRoleCode;
}

export interface IntegrationTokenDelegationPermission {
  roleCode: IntegrationTokenDelegationRoleCode;
  canCreateProjects?: boolean;
  canViewFinances?: boolean;
  canEditFinances?: boolean;
  projectPermissions: IntegrationTokenDelegationProjectPermission[];
}

export interface IntegrationTokenDelegation {
  id: string;
  clientId: string;
  clientUserId: string; // user who granted the delegation
  userId: string; // user who granted the delegation
  tokenId: string; // integration token the delegation is attached to
  tokenPermissions: IntegrationTokenDelegationPermission;
  created: string;
  lastUpdate: string;
}

export interface IntegrationTokenDelegationListResponse {
  list: IntegrationTokenDelegation[];
}

// Create body — only the `tokenPermissions` blueprint. `tokenId` comes from the
// URL and `userId`/`clientUserId` from the authenticated caller.
export interface IntegrationTokenDelegationRequest {
  roleCode: IntegrationTokenDelegationRoleCode;
  canCreateProjects?: boolean;
  canViewFinances?: boolean;
  canEditFinances?: boolean;
  projectPermissions: Array<Pick<IntegrationTokenDelegationProjectPermission, 'projectId' | 'roleCode'>>;
}
